import { EProjectAuthType, ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { APP_AUTH_GENERIC_SEARCH_TERMS } from "../app-settings-config-auth/app-settings-config-auth-generic";
import { APP_AUTH_LDAP_SEARCH_TERMS } from "../app-settings-config-auth/app-settings-config-auth-ldap";
import { APP_AUTH_OAUTH2_SEARCH_TERMS } from "../app-settings-config-auth/app-settings-config-auth-oauth2";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect, IConfigSettingOption } from "../config-setting-select";
import { ConfigFormAuthSettings, FORM_AUTH_SETTINGS_SEARCH_TERMS } from "./app-settings-config-form-authentication";

export interface IConfigFormAuthInputState {
  showModal: boolean;
  editingFormAuthId: string;
}

/** A provider the dropdown can offer: a saved setting, or the built-in "no provider" row. */
export interface IFormAuthSetting {
  id: string | null;
  name: string;
  type: EProjectAuthType;
  isGlobalCommonConfigOnProjectConfig?: boolean;
}

export interface IConfigFormAuthInputProps {
  isRunConfig?: boolean;
  parentConfigValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

/**
 * Every field the Authentication provider row can reach. The dialog picks a Type and then
 * draws that provider's fields in the same modal, so the four lists are one row's worth.
 */
const AUTHENTICATION_SEARCH_TERMS: string[] = [
  ...FORM_AUTH_SETTINGS_SEARCH_TERMS,
  ...APP_AUTH_LDAP_SEARCH_TERMS,
  ...APP_AUTH_OAUTH2_SEARCH_TERMS,
  ...APP_AUTH_GENERIC_SEARCH_TERMS
];

export class ConfigFormAuthInput extends PureComponent<IConfigFormAuthInputProps, IConfigFormAuthInputState> {
  formInputName = ["apiConfig", "authProviderSettingsId"];

  constructor(props: IConfigFormAuthInputProps) {
    super(props);
    this.state = {
      showModal: false,
      editingFormAuthId: ""
    };
  }

  getFormAuthSettings = (): IFormAuthSetting[] => {
    const genericAuthSettings = this.props.commonSettings.genericAuthSettings?.map((setting) => ({
      ...setting,
      type: EProjectAuthType.genericAuth
    }));
    const oAuth2Settings = this.props.commonSettings.oAuth2Settings?.map((setting) => ({
      ...setting,
      type: EProjectAuthType.oAuth2
    }));
    const ldapSettings = this.props.commonSettings.ldapSettings?.map((setting) => ({
      ...setting,
      type: EProjectAuthType.ldap
    }));
    // const msalSettings = this.props.sd?.commonSettings.msalSettings?.map((setting) => ({
    //   ...setting,
    //   type: EProjectAuthType.msal
    // }));

    let formAuthSettings: IFormAuthSetting[] = [];
    if (genericAuthSettings) {
      formAuthSettings = formAuthSettings.concat(genericAuthSettings);
    }
    if (oAuth2Settings) {
      formAuthSettings = formAuthSettings.concat(oAuth2Settings);
    }
    if (ldapSettings) {
      formAuthSettings = formAuthSettings.concat(ldapSettings);
    }
    // if (msalSettings) {
    //   formAuthSettings = formAuthSettings.concat(msalSettings);
    // }
    const isMainAi = KMainFunctions.isMainAI();
    formAuthSettings.push({
      id: null,
      name: isMainAi ? "Main AI Auth" : "Kuika Auth",
      type: EProjectAuthType.none
    });
    return formAuthSettings;
  };

  openModal = () => {
    this.setState({ editingFormAuthId: "", showModal: true });
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  setEditingFormAuth = (id: string) => {
    this.setState({ editingFormAuthId: id, showModal: true });
  };

  handleRestore = (): void => {
    this.props.handleUpdateCustomFormInput?.(this.formInputName, this.props.parentConfigValue ?? null);
  };

  /**
   * The list the dropdown shows.
   *
   * "None" sorts to the top and is the one provider with nothing behind it to open, so it
   * is marked non-editable rather than being given a dead pencil — which is what the
   * hand-built option did with `opacity: 0` and a click that checked the type again.
   */
  getOptions = (): IConfigSettingOption[] => {
    const settings: IFormAuthSetting[] = [...this.getFormAuthSettings()];
    settings.sort((a: IFormAuthSetting, b: IFormAuthSetting): number => {
      if (a.type === EProjectAuthType.none) return -1;
      if (b.type === EProjectAuthType.none) return 1;
      return 0;
    });
    return settings.map((setting: IFormAuthSetting): IConfigSettingOption => ({
      ...setting,
      editable: setting.type !== EProjectAuthType.none
    }));
  };

  render() {
    const formAuthSettings = this.getFormAuthSettings();
    const {
      isRunConfig,
      parentConfigValue,
      commonSettings,
      getSectionDescription,
      handleUpdateCustomFormInput,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;
    return (
      <>
        <ConfigFormAuthSettings
          formInputName={this.formInputName}
          closeModal={this.closeModal}
          showModal={this.state.showModal}
          editingFormAuthId={this.state.editingFormAuthId}
          formAuthSettings={formAuthSettings}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
          isRunConfig={isRunConfig}
        />
        <ConfigSettingSelect
          // One row opens all four: the dialog asks for a Type and then draws that
          // provider's own fields inline, so LDAP's Search Template and oAuth's Scope are
          // both reachable from here and neither has a row of its own to be found by.
          searchTerms={AUTHENTICATION_SEARCH_TERMS}
          label="Authentication provider"
          formInputName={this.formInputName}
          options={this.getOptions()}
          tooltipContentKey="appsettings_authentication_authorization_authenticationprovider"
          onAddNew={this.openModal}
          onEdit={this.setEditingFormAuth}
          parentConfigValue={this.props.parentConfigValue}
          onRestore={this.handleRestore}
          dropdownTestId="appSettingsAuthProviderDropdown"
          addNewTestId="appSettingsAuthProviderAddNew"
          editTestId="appSettingsAuthProviderEditButton"
        />
      </>
    );
  }
}
